<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { getAlmInfo, loadSessionAlmStatus } from "@/backend/read/BackendReader";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import { cockpitConnectionUrl } from "@/components/utils/cockpitUrls";
import type { InfoLevel } from "@/model/baseTypes";
import type { SessionAlmStatus } from "@/model/session";
import { useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";

const { t } = useI18n();

onMounted(async () => await loadSessionAlmStatus());

const status = computed(() => {
  const almType = getAlmInfo().name;
  const almStatus = useSessionStore().current.almStatus || "running";
  const mapping: Record<SessionAlmStatus, { message: string; type: InfoLevel }> = {
    stopped: { message: t("almsync.stopped", { almType }), type: "default" },
    error: { message: t("almsync.disconnected", { almType }), type: "error" },
    starting: { message: t("almsync.resync"), type: "ok" },
    running: { message: "", type: "ok" },
  };
  return mapping[almStatus];
});

const cockpitAllowed = computed(() => useUserStore().isAllowed("cockpit"));
const canAdmin = computed(() => status.value.type === "error" && cockpitAllowed.value);

const attrs = computed(() =>
  canAdmin.value
    ? { as: "a" as const, target: "_blank", href: cockpitConnectionUrl() }
    : { as: "div" as const },
);
</script>

<template>
  <div id="alm-status" data-no-screenshot>
    <BaseTooltip :key="canAdmin ? 'withTooltip' : 'withoutTooltip'" position="bottom">
      <Transition name="fade">
        <BaseButton
          v-if="status.message"
          v-bind="attrs"
          tabindex="0"
          variant="ghost"
          data-testid="alm-status"
        >
          <InfoMarker class="info-marker" :level="status.type" :top="2" />
          <span>{{ status.message }}</span>
        </BaseButton>
      </Transition>
      <template v-if="canAdmin" #content>
        <div class="alm-status-tooltip">
          {{ $t("almSync.tooltip") }}
        </div>
      </template>
    </BaseTooltip>
  </div>
  <MenuItemDelimiter v-if="status.message" />
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/variables/colors";

#alm-status {
  .info-marker {
    display: inline-block;
    position: relative;
    margin: 0 6px;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.75s ease-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}

.alm-status-tooltip {
  width: 240px;
}
</style>
